import { useState } from "react";
import { Search, ShoppingCart, Shield, Truck, Award, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";

type ProductSource = "OEM" | "FixID Network";
type ProductCondition = "New" | "Refurbished" | "Used";

interface Product {
  id: string;
  name: string;
  partNumber: string;
  source: ProductSource;
  condition: ProductCondition;
  oemPrice: number;
  fixidPrice: number;
  compatibleWith?: string[];
  inStock: boolean;
}

const products: Product[] = [
  { id: "1", name: "Premium Door Boot Seal", partNumber: "BSH-354135-PRO", source: "OEM", condition: "New", oemPrice: 125.00, fixidPrice: 89.99, compatibleWith: ["WAE284665N/36", "WAE28466GB/08"], inStock: true },
  { id: "2", name: "Carbon Brush Set", partNumber: "BSH-481242-CB", source: "FixID Network", condition: "New", oemPrice: 45.00, fixidPrice: 28.99, compatibleWith: ["WAE284665N/36"], inStock: true },
  { id: "3", name: "Digital Control Module", partNumber: "BSH-754209-DCM", source: "OEM", condition: "New", oemPrice: 289.00, fixidPrice: 185.00, compatibleWith: ["WAE284665N/36", "WAF28470GB"], inStock: true },
  { id: "4", name: "Motor brush assembly", partNumber: "BSH-096431-MBA", source: "FixID Network", condition: "Refurbished", oemPrice: 95.00, fixidPrice: 55.00, compatibleWith: ["WAE284665N/36"], inStock: true },
  { id: "5", name: "Drain Pump Assembly", partNumber: "BSH-145326-DPA", source: "OEM", condition: "New", oemPrice: 95.00, fixidPrice: 65.50, compatibleWith: ["WAE284665N/36", "WAE28466GB/08"], inStock: true },
  { id: "6", name: "Water Inlet Valve", partNumber: "BSH-263821-WIV", source: "FixID Network", condition: "New", oemPrice: 68.00, fixidPrice: 45.75, compatibleWith: ["WAE284665N/36"], inStock: true },
  { id: "7", name: "Electronic Door Lock", partNumber: "BSH-174360-EDL", source: "OEM", condition: "New", oemPrice: 112.00, fixidPrice: 78.50, compatibleWith: ["WAE284665N/36", "WAE28466GB/08"], inStock: true },
  { id: "8", name: "Drive Belt Set", partNumber: "BSH-481242-DBS", source: "FixID Network", condition: "New", oemPrice: 35.00, fixidPrice: 25.99, compatibleWith: ["WAE284665N/36", "WAF28470GB"], inStock: true },
  { id: "9", name: "Lint Filter Assembly", partNumber: "BSH-126784-LFA", source: "FixID Network", condition: "Used", oemPrice: 32.00, fixidPrice: 18.99, compatibleWith: ["WAE284665N/36"], inStock: true },
  { id: "10", name: "Upper Spray Arm Assembly", partNumber: "MIE-263821-USA", source: "OEM", condition: "New", oemPrice: 55.00, fixidPrice: 35.99, compatibleWith: ["G4263Vi/01"], inStock: true },
  { id: "11", name: "Compressor Unit 550watt", partNumber: "LG-CU5500-CU", source: "OEM", condition: "Refurbished", oemPrice: 385.00, fixidPrice: 275.00, compatibleWith: ["LRMVS3006S"], inStock: true },
  { id: "12", name: "Digital Thermostat Control", partNumber: "GEN-00082-DTC", source: "FixID Network", condition: "New", oemPrice: 125.00, fixidPrice: 89.99, compatibleWith: ["GNE27JSMSS"], inStock: true },
  { id: "13", name: "HEPA Filter Pro", partNumber: "MIE-SF-HA50-PRO", source: "OEM", condition: "New", oemPrice: 65.00, fixidPrice: 44.99, compatibleWith: ["Complete C3", "Complete C2"], inStock: true },
  { id: "14", name: "Professional Torx Screwdriver Set", partNumber: "TOOL-TRX-12-PRO", source: "FixID Network", condition: "New", oemPrice: 89.00, fixidPrice: 65.99, inStock: true },
  { id: "15", name: "Professional Silicon Set", partNumber: "SILN-PRF-03-SET", source: "OEM", condition: "New", oemPrice: 225.00, fixidPrice: 175.00, inStock: true },
  { id: "16", name: "Safety Glasses Professional", partNumber: "SAFETY-GLASS-PRO", source: "FixID Network", condition: "New", oemPrice: 28.00, fixidPrice: 18.99, inStock: true },
  { id: "17", name: "Eco-Friendly Appliance Cleaner", partNumber: "CLEAN-ECO-500-PRO", source: "FixID Network", condition: "New", oemPrice: 18.00, fixidPrice: 12.99, inStock: true },
];

const sourceColors: Record<ProductSource, { bg: string; text: string }> = {
  "OEM": { bg: "bg-purple-100", text: "text-purple-700" },
  "FixID Network": { bg: "bg-green-100", text: "text-green-700" },
};

const conditionColors: Record<ProductCondition, { bg: string; text: string }> = {
  "New": { bg: "bg-blue-100", text: "text-blue-700" },
  "Refurbished": { bg: "bg-yellow-100", text: "text-yellow-700" },
  "Used": { bg: "bg-gray-100", text: "text-gray-600" },
};

export default function Store() {
  const { t } = useTranslation("store");
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [conditionFilter, setConditionFilter] = useState("all");
  const [sortBy, setSortBy] = useState("relevance");

  const stats = [
    { icon: Shield, label: t("list.oemQuality"), sublabel: t("list.oemQualityDesc") },
    { icon: Award, label: t("list.networkPricing"), sublabel: t("list.networkPricingDesc") },
    { icon: Truck, label: t("list.fastShipping"), sublabel: t("list.fastShippingDesc") },
    { icon: Clock, label: t("list.partWarranty"), sublabel: t("list.partWarrantyDesc") },
  ];

  const handleAddToCart = (product: Product) => {
    toast.success(t("card.addedToCart", { name: product.name }));
  };

  const filteredProducts = products.filter((product) => {
    const matchesSearch = product.name.toLowerCase().includes(searchQuery.toLowerCase()) || product.partNumber.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCondition = conditionFilter === "all" || product.condition.toLowerCase() === conditionFilter;
    return matchesSearch && matchesCondition;
  });

  const sortedProducts = [...filteredProducts].sort((a, b) => {
    if (sortBy === "price-low") return a.fixidPrice - b.fixidPrice;
    if (sortBy === "price-high") return b.fixidPrice - a.fixidPrice;
    if (sortBy === "savings") {
      const sa = ((a.oemPrice - a.fixidPrice) / a.oemPrice) * 100;
      const sb = ((b.oemPrice - b.fixidPrice) / b.oemPrice) * 100;
      return sb - sa;
    }
    return 0;
  });

  const conditionLabel = (c: ProductCondition) => c === "New" ? t("list.new") : c === "Refurbished" ? t("list.refurbished") : t("list.used");
  const sourceLabel = (s: ProductSource) => s === "OEM" ? t("card.sourceOEM") : t("card.sourceNetwork");

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="px-6 pt-8 pb-6">
        <h1 className="text-2xl font-bold text-foreground mb-1">{t("list.title")}</h1>
        <p className="text-muted-foreground">{t("list.subtitle")}</p>
      </div>

      <div className="px-6 mb-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat, index) => {
            const Icon = stat.icon;
            return (
              <div key={index} className="bg-card rounded-xl border border-border p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                  <Icon className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="font-medium text-foreground text-sm">{stat.label}</p>
                  <p className="text-xs text-muted-foreground">{stat.sublabel}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="px-6 mb-6 space-y-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input placeholder={t("list.search")} value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10" />
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder={t("list.allCategories")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("list.allCategories")}</SelectItem>
              <SelectItem value="appliances">{t("list.appliances")}</SelectItem>
              <SelectItem value="tools">{t("list.tools")}</SelectItem>
            </SelectContent>
          </Select>

          <Select defaultValue="all">
            <SelectTrigger className="w-[140px]"><SelectValue placeholder={t("list.appliances")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("list.allAppliances")}</SelectItem>
              <SelectItem value="washing">{t("list.washingMachines")}</SelectItem>
              <SelectItem value="dishwasher">{t("list.dishwashers")}</SelectItem>
              <SelectItem value="refrigerator">{t("list.refrigerators")}</SelectItem>
            </SelectContent>
          </Select>

          <Select defaultValue="all">
            <SelectTrigger className="w-[140px]"><SelectValue placeholder={t("list.manufacturer")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("list.allManufacturers")}</SelectItem>
              <SelectItem value="bosch">Bosch</SelectItem>
              <SelectItem value="miele">Miele</SelectItem>
              <SelectItem value="samsung">Samsung</SelectItem>
            </SelectContent>
          </Select>

          <Select value={conditionFilter} onValueChange={setConditionFilter}>
            <SelectTrigger className="w-[140px]"><SelectValue placeholder={t("list.allConditions")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("list.allConditions")}</SelectItem>
              <SelectItem value="new">{t("list.new")}</SelectItem>
              <SelectItem value="refurbished">{t("list.refurbished")}</SelectItem>
              <SelectItem value="used">{t("list.used")}</SelectItem>
            </SelectContent>
          </Select>

          <Select value={sortBy} onValueChange={setSortBy}>
            <SelectTrigger className="w-[160px]"><SelectValue placeholder={t("list.sortRelevance")} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="relevance">{t("list.sortRelevance")}</SelectItem>
              <SelectItem value="price-low">{t("list.priceLowHigh")}</SelectItem>
              <SelectItem value="price-high">{t("list.priceHighLow")}</SelectItem>
              <SelectItem value="savings">{t("list.bestSavings")}</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="px-6">
        <p className="text-sm text-muted-foreground mb-4">{t("list.showingResults", { count: sortedProducts.length })}</p>

        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {sortedProducts.map((product) => {
            const savings = Math.round(((product.oemPrice - product.fixidPrice) / product.oemPrice) * 100);
            return (
              <div key={product.id} className="bg-card rounded-xl border border-border overflow-hidden hover:border-primary/50 transition-colors">
                <div className="aspect-square bg-muted flex items-center justify-center">
                  <span className="text-4xl text-muted-foreground">🔧</span>
                </div>

                <div className="p-4">
                  <div className="flex flex-wrap items-center gap-1.5 mb-2">
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${sourceColors[product.source].bg} ${sourceColors[product.source].text}`}>
                      {sourceLabel(product.source)}
                    </span>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded ${conditionColors[product.condition].bg} ${conditionColors[product.condition].text}`}>
                      {conditionLabel(product.condition)}
                    </span>
                  </div>

                  <h4 className="font-medium text-foreground text-sm mb-1 line-clamp-2">{product.name}</h4>
                  <p className="text-xs text-muted-foreground mb-3">{product.partNumber}</p>

                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-sm text-muted-foreground line-through">€{product.oemPrice.toFixed(2)}</span>
                    <span className="text-lg font-bold text-refurbish">€{product.fixidPrice.toFixed(2)}</span>
                  </div>

                  <p className="text-xs text-refurbish font-medium mb-2">{t("card.savePct", { pct: savings })}</p>

                  {product.compatibleWith && (
                    <p className="text-xs text-muted-foreground mb-3 line-clamp-1">
                      {t("card.compatible", { model: product.compatibleWith[0] })}
                    </p>
                  )}

                  <Button onClick={() => handleAddToCart(product)} className="w-full bg-refurbish hover:bg-refurbish/90 text-refurbish-foreground" size="sm">
                    <ShoppingCart className="w-4 h-4 mr-2" />
                    {t("card.addToCart")}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
